import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";

const skills = [
    { label: "Paid search & growth", value: 92 },
    { label: "Search engine optimization", value: 95 },
    { label: "Content velocity", value: 88 },
    { label: "Creative testing", value: 90 },
];

export default function DigtekAbout() {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setInView(true),
            { threshold: 0.3 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <section
            id="about"
            data-testid="digtek-about"
            ref={ref}
            className="relative py-24 lg:py-32 bg-digtek-bg overflow-hidden"
        >
            <div className="digtek-blob purple w-[400px] h-[400px] -right-32 top-24 opacity-50" />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                {/* Left — image collage */}
                <div className="lg:col-span-5 relative">
                    <div className="relative">
                        <div className="absolute -top-6 -left-6 w-full h-full bg-digtek-purple rounded-[28px]" />
                        <img
                            src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                            alt="Aximo team at work"
                            className="relative w-full aspect-[4/5] object-cover rounded-[28px]"
                        />

                        {/* Floating brand badge */}
                        <div className="absolute -top-6 right-6 bg-white rounded-2xl px-5 py-4 shadow-[0_20px_50px_-10px_rgba(23,1,44,.15)]">
                            <p className="text-[11px] uppercase tracking-widest text-digtek-text font-semibold mb-1">
                                Brands trust us
                            </p>
                            <p className="font-display font-bold text-3xl text-digtek-header leading-none">
                                1K+
                            </p>
                        </div>

                        {/* Floating emoji badge */}
                        <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-2xl bg-digtek-lime flex items-center justify-center text-4xl shadow-[0_20px_50px_-10px_rgba(198,248,6,.5)] rotate-6">
                            🚀
                        </div>
                    </div>
                </div>

                {/* Right — content */}
                <div className="lg:col-span-7">
                    <div className="digtek-eyebrow mb-4">
                        <span>About the studio</span>
                    </div>
                    <h2
                        data-testid="about-heading"
                        className="font-display font-bold text-[40px] sm:text-[52px] lg:text-[58px] leading-[1.02] text-digtek-header mb-6"
                    >
                        The skills that{" "}
                        <em className="not-italic text-digtek-purple">
                            move your brand
                        </em>{" "}
                        forward.
                    </h2>
                    <p className="text-digtek-text text-lg leading-relaxed mb-10 max-w-[560px]">
                        We're a growth studio, not a job shop. Every engagement
                        is scoped to make a number move — pipeline, retention,
                        share-of-voice — and instrumented so we can prove it did.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6 mb-10">
                        {skills.map((s) => (
                            <div key={s.label} data-testid={`skill-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                                <div className="flex justify-between items-baseline mb-2">
                                    <span className="text-sm font-semibold text-digtek-header">
                                        {s.label}
                                    </span>
                                    <span className="font-display font-bold text-digtek-purple">
                                        {s.value}%
                                    </span>
                                </div>
                                <div className="digtek-skillbar">
                                    <div
                                        className="digtek-skillbar-fill"
                                        style={{
                                            width: inView ? `${s.value}%` : "0%",
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
                        {[
                            "30-day money back guarantee",
                            "Weekly reporting rituals",
                            "Unlimited creative revisions",
                            "24/7 team on Slack",
                        ].map((b) => (
                            <li
                                key={b}
                                className="flex items-center gap-3 text-digtek-header font-medium"
                            >
                                <span className="w-6 h-6 rounded-full bg-digtek-lime flex items-center justify-center shrink-0">
                                    <Check className="w-3.5 h-3.5 text-digtek-header stroke-[3]" />
                                </span>
                                {b}
                            </li>
                        ))}
                    </ul>

                    <a
                        href="#work"
                        data-testid="about-cta"
                        className="btn-digtek"
                    >
                        Explore more
                        <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </section>
    );
}
